import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { hydrateCandidates } from '../engine/candidateData'

/**
 * useCandidates — fetches candidates + positions from Supabase
 *
 * Two ways to narrow to a district, and they are not equivalent:
 *
 *   districtGeoid — the layer-namespaced Census key ('sldu:51014'). Uniform
 *     nationwide, and what districtLookup (#419) returns for a voter address.
 *     Use this one.
 *   district — the display label ('Senate District 14'). Still honoured for
 *     existing callers, but it only matches where the stored row happens to
 *     phrase the label the same way. In New York the same district reads
 *     'Assembly District 75' and the label query returns nothing — which is
 *     indistinguishable, to a voter, from having no races at all.
 *
 * When both are given the GEOID wins and the label is ignored, so a caller
 * migrating between them cannot accidentally AND the two into an empty result.
 *
 * @param {Object} options
 * @param {string} [options.state]         — filter by state code e.g. 'VA'
 * @param {string} [options.districtGeoid] — filter by namespaced Census key
 * @param {string} [options.district]      — filter by display label (legacy)
 * @returns {{ candidates, loading, error }}
 */
export function useCandidates({ state, district, districtGeoid } = {}) {
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function fetchCandidates() {
      setLoading(true)
      setError(null)

      try {
        // ── 1. Fetch candidates ────────────────────────────────────
        const candidateRows = []
        const pageSize = 200
        for (let page = 0; ; page++) {
          if (page >= 100) throw new Error('Candidate list is too large. Select a state to narrow the search.')
          let query = supabase.from('candidates').select('*')
            .order('state', { ascending: true }).order('name', { ascending: true }).order('id', { ascending: true })
          if (state) query = query.eq('state', state)
          if (districtGeoid) query = query.eq('district_geoid', districtGeoid)
          else if (district) query = query.eq('district', district)
          const { data, error: candError } = await query.range(page * pageSize, (page + 1) * pageSize - 1)
          if (candError) throw candError
          candidateRows.push(...(data || []))
          if (cancelled) return
          if (!data || data.length < pageSize) break
        }
        if (!candidateRows?.length) {
          if (!cancelled) { setCandidates([]); setLoading(false) }
          return
        }

        const merged = await hydrateCandidates(candidateRows)

        if (!cancelled) {
          setCandidates(merged)
          setLoading(false)
        }
      } catch (err) {
        console.error('[useCandidates] fetch error:', err)
        if (!cancelled) {
          setError(err.message || 'Failed to load candidates')
          setLoading(false)
        }
      }
    }

    fetchCandidates()
    return () => { cancelled = true }
  }, [state, district, districtGeoid])

  return { candidates, loading, error }
}

export default useCandidates
